import { cn } from '../../lib/cn'
import { useRovingRadioGroup } from './useRovingRadioGroup'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
}

interface SegmentedControlProps<T extends string> {
  /** Names the group for a screen reader — "Sort the register", not "Sort". */
  label: string
  options: SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
}

/**
 * The register card's segmented control — one of a small set of views over the
 * SAME data, never a filter that hides rows.
 *
 * A RADIOGROUP rather than a tablist or a row of buttons, because that is what
 * it is: exactly one of N is chosen, and the choice re-sorts what is already on
 * the page. Two consequences that are the whole reason this is a component:
 *
 *   * **Roving tabindex.** A radiogroup takes ONE tab stop, not three. The
 *     selected option carries `tabIndex=0` and the rest `-1`; the arrow keys
 *     move between them (and select as they go, the native radio behaviour),
 *     and Home/End reach the ends. Three separate tab stops would be the
 *     button-row pattern, and it is the wrong one — a keyboard user tabbing
 *     through the page would land in the control three times. That half is
 *     `useRovingRadioGroup`, shared with the hole rail and the hole gallery.
 *   * **`aria-checked`, not `aria-selected`.** A screen reader announces "2 of
 *     3, selected"; `aria-pressed` (a toggle) would let it say two are on.
 *
 * Colour never carries the state alone: the chosen segment takes the pine
 * ground AND the checked state, so it reads the same to a reader that ignores
 * every pixel.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: SegmentedControlProps<T>) {
  const group = useRovingRadioGroup(options.length, (index) => onChange(options[index].value))

  return (
    <div
      role="radiogroup"
      aria-label={label}
      // Wraps rather than scrolls or hides below 860px: three words on two
      // lines is a worse layout and a working control.
      className={cn(
        'flex flex-wrap gap-1 rounded-club-sm border border-brass/45 bg-parchment p-1',
        className,
      )}
    >
      {options.map((option, index) => {
        const checked = option.value === value
        return (
          <button
            key={option.value}
            ref={group.optionRef(index)}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => group.onKeyDown(event, index)}
            className={cn(
              'rounded-club-sm px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em]',
              checked ? 'bg-pine text-paper' : 'text-brass-deep',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
